import { api } from "./client";
import type { PagedQuery, PagedResult } from "@/types/api";

// LLM & Reports v3.1 — frontend API client. The unified ReportTemplate
// aggregate replaces v2.0's split PromptTemplate + FormTemplate model;
// every report originates from a submitted ReportSubmission and routes
// through POST /api/reports/generate.

export type OutputFormat = "Word" | "Excel" | "Pdf";
export type TemplateCategory =
  | "LoanLevel"
  | "PortfolioLevel"
  | "BrokerLevel"
  | "VendorLevel"
  | "DealLevel"
  | "General";

export type QuestionType =
  | "Text"
  | "TextArea"
  | "Number"
  | "Date"
  | "Select"
  | "MultiSelect"
  | "YesNo"
  | "RatingScale"
  | "FileAttachment"
  | "LoanPicker"
  | "PortfolioPicker"
  | "BrokerPicker"
  | "VendorPicker"
  | "DealPicker";

/**
 * User-facing label for a QuestionType. The internal enum value (which
 * still travels on the wire and is the column value the database
 * stores) is unchanged; only the display string changes. Use this
 * function anywhere a question type is rendered to a user — dropdowns,
 * summary chips, badges, etc.
 */
export function questionTypeLabel(type: QuestionType): string {
  switch (type) {
    case "Text":
      return "Short answer";
    case "TextArea":
      return "Long answer";
    case "Number":
      return "Number";
    case "Date":
      return "Date";
    case "Select":
      return "Choose one";
    case "MultiSelect":
      return "Choose multiple";
    case "YesNo":
      return "Yes / No";
    case "RatingScale":
      return "Rating (1–5)";
    case "FileAttachment":
      return "Attach file";
    case "LoanPicker":
      return "Pick a loan";
    case "PortfolioPicker":
      return "Pick a portfolio";
    case "BrokerPicker":
      return "Pick a broker";
    case "VendorPicker":
      return "Pick a vendor";
    case "DealPicker":
      return "Pick a deal";
  }
}

export type SubmissionStatus = "Draft" | "Submitted" | "Generating" | "Completed";

/**
 * LLM &amp; Reports v3.4/v3.5 — sentinel identifiers for the standard
 * virtual sections and questions injected onto user-facing template
 * reads. The admin editor's adminGet route returns the raw schema
 * without these; the fill-out form's get route includes them.
 *
 * Mirrors the C# `ReportSystemFields` registry. Frontend uses the
 * question ids to special-case rendering of the Report context
 * fields (top of the form, separate from the responses array).
 */
export const SYSTEM_SECTION_ID = "ffffffff-0000-0000-0000-000000000002";
export const PREPARED_FOR_QUESTION_ID = "ffffffff-0000-0000-0000-000000000001";
/** v3.5 §5: new "Prepared for / Audience" virtual question id. */
export const AUDIENCE_QUESTION_ID = "ffffffff-0000-0000-0000-000000000003";

export function isSystemSection(sectionId: string): boolean {
  return sectionId.toLowerCase() === SYSTEM_SECTION_ID;
}

export function isSystemQuestion(questionId: string): boolean {
  const lower = questionId.toLowerCase();
  return lower === PREPARED_FOR_QUESTION_ID || lower === AUDIENCE_QUESTION_ID;
}

export interface ReportFormQuestionDto {
  id: string;
  questionText: string;
  helpText?: string | null;
  questionType: QuestionType;
  optionsJson?: string | null;
  validationRulesJson?: string | null;
  isRequired: boolean;
  displayOrder: number;
}

export interface ReportFormSectionDto {
  id: string;
  title: string;
  description?: string | null;
  displayOrder: number;
  isRequired: boolean;
  questions: ReportFormQuestionDto[];
}

export interface ReportTemplateDto {
  id: string;
  name: string;
  description: string;
  promptBody: string;
  outputFormat: OutputFormat;
  category: TemplateCategory;
  masterDocumentId?: string | null;
  instructions?: string | null;
  version: number;
  isActive: boolean;
  /**
   * LLM &amp; Reports v3.2 §12.2 — when true, the frontend renders the
   * v3.2 attachment-aware generate flow; when false (or absent on
   * legacy DTOs), the v3.1 single-button path. Optional in the type
   * so the decoder is tolerant of older backends that don't surface
   * this column yet.
   */
  usesV32GenerationFlow?: boolean | null;
  roleCodes: string[];
  sections: ReportFormSectionDto[];
  createdAt: string;
  modifiedAt?: string | null;
  /** Number of GeneratedReport rows that reference this template. Drives
   * the disabled state of the Delete action on the admin list. */
  generatedReportCount: number;
}

export interface UpsertReportFormQuestionPayload {
  id?: string | null;
  questionText: string;
  helpText?: string | null;
  questionType: QuestionType;
  optionsJson?: string | null;
  validationRulesJson?: string | null;
  isRequired: boolean;
  displayOrder: number;
}

export interface UpsertReportFormSectionPayload {
  id?: string | null;
  title: string;
  description?: string | null;
  displayOrder: number;
  isRequired: boolean;
  questions: UpsertReportFormQuestionPayload[];
}

export interface UpsertReportTemplatePayload {
  name: string;
  description: string;
  promptBody: string;
  outputFormat: OutputFormat;
  category: TemplateCategory;
  masterDocumentId?: string | null;
  instructions?: string | null;
  roleCodes: string[];
  isActive: boolean;
  sections: UpsertReportFormSectionPayload[];
}

export interface ReportResponseDto {
  id: string;
  formQuestionId: string;
  responseText?: string | null;
  responseValueJson?: string | null;
  attachedDocumentIdsJson?: string | null;
}

export interface ReportSubmissionDto {
  id: string;
  formTemplateId: string;
  formTemplateName: string;
  userId: string;
  status: SubmissionStatus;
  submittedAt?: string | null;
  generatedReportId?: string | null;
  responses: ReportResponseDto[];
  createdAt: string;
  /** v3.4 §1 / v3.5 §5: "Report subject" virtual field value. Null when blank. */
  preparedFor?: string | null;
  /** v3.5 §5: "Prepared for / Audience" virtual field value. Null when blank. */
  audience?: string | null;
}

export interface CreateReportSubmissionPayload {
  formTemplateId: string;
  loanId?: string | null;
  brokerId?: string | null;
  dealId?: string | null;
  vendorId?: string | null;
  portfolioId?: string | null;
  responses: {
    formQuestionId: string;
    responseText?: string | null;
    responseValueJson?: string | null;
    attachedDocumentIdsJson?: string | null;
  }[];
  /** v3.4 §1 / v3.5 §5: "Report subject" virtual field. Null when blank. */
  preparedFor?: string | null;
  /** v3.5 §5: "Prepared for / Audience" virtual field. Null when blank. */
  audience?: string | null;
}

export interface GenerateReportPayload {
  reportTemplateId: string;
  title: string;
  reportSubmissionId: string;
  supportingDocumentIds?: string[] | null;
  oneTimePromptEdit?: string | null;
}

export interface GenerateReportResult {
  reportId: string;
  title: string;
  blobPath?: string | null;
  downloadUrl?: string | null;
  generatedContent: string;
}

/**
 * Deal work-tab "Generate report" deep link — pre-drafted generator intake
 * resolved by underwriting catalog code. Null data from the endpoint means no
 * active template carries the code for the caller's role (admin-added catalog
 * items, archived templates) — callers hide the button.
 */
export interface ReportItemPrefillDto {
  templateId: string;
  templateName: string;
  /** Resolved live server-side; null if the goal section was restructured away. */
  goalQuestionId?: string | null;
  title: string;
  subject: string;
  audience: string;
  /** Deal placeholders already substituted; null when the template has no default. */
  goalText?: string | null;
}

export interface TestReportTemplateRequest {
  reportSubmissionId?: string | null;
  supportingDocumentIds?: string[] | null;
  oneTimePromptEdit?: string | null;
}

export interface TestReportTemplateResponse {
  resolvedPrompt: string;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";

export const reportTemplatesApi = {
  // User-facing.
  mine: () => api.get<ReportTemplateDto[]>("/report-templates/mine"),
  get: (id: string) => api.get<ReportTemplateDto>(`/report-templates/${id}`),
  itemPrefill: (itemCode: string, dealId: string) =>
    api.get<ReportItemPrefillDto | null>("/report-templates/item-prefill", {
      itemCode,
      dealId,
    }),

  // Admin.
  adminList: (query?: PagedQuery) =>
    api.get<PagedResult<ReportTemplateDto>>("/admin/report-templates", query),
  /**
   * v3.4 §1 — admin-only raw schema read. The template editor uses
   * this so the standard `_preparedFor` virtual section isn't surfaced
   * as an editable row. The user-facing `get(id)` route still returns
   * the augmented schema with the system section prepended.
   */
  adminGet: (id: string) =>
    api.get<ReportTemplateDto>(`/admin/report-templates/${id}`),
  adminCreate: (body: UpsertReportTemplatePayload) =>
    api.post<ReportTemplateDto>("/admin/report-templates", body),
  adminUpdate: (id: string, body: UpsertReportTemplatePayload) =>
    api.put<ReportTemplateDto>(`/admin/report-templates/${id}`, body),
  adminDelete: (id: string) => api.delete<void>(`/admin/report-templates/${id}`),
  adminArchive: (id: string) =>
    api.post<ReportTemplateDto>(`/admin/report-templates/${id}/archive`),
  adminUnarchive: (id: string) =>
    api.post<ReportTemplateDto>(`/admin/report-templates/${id}/unarchive`),
  adminUpdateRoles: (id: string, roleCodes: string[]) =>
    api.put<ReportTemplateDto>(`/admin/report-templates/${id}/roles`, { roleCodes }),
  adminUpdateMasterDocument: (id: string, masterDocumentId: string | null) =>
    api.put<ReportTemplateDto>(`/admin/report-templates/${id}/master-document`, {
      masterDocumentId,
    }),
  adminCopyFormFrom: (id: string, sourceId: string) =>
    api.post<ReportTemplateDto>(
      `/admin/report-templates/${id}/copy-form-from/${sourceId}`,
    ),
  adminTest: (id: string, body: TestReportTemplateRequest) =>
    api.post<TestReportTemplateResponse>(`/admin/report-templates/${id}/test`, body),
};

export const reportSubmissionsApi = {
  mine: (query?: PagedQuery) =>
    api.get<PagedResult<ReportSubmissionDto>>("/report-submissions", query),
  // Returns the embedded form (sections + questions) for a template.
  form: (reportTemplateId: string) =>
    api.get<ReportTemplateDto>(
      `/report-submissions/templates/${reportTemplateId}/form`,
    ),
  create: (body: CreateReportSubmissionPayload) =>
    api.post<ReportSubmissionDto>("/report-submissions", body),
  get: (id: string) => api.get<ReportSubmissionDto>(`/report-submissions/${id}`),
  updateDraft: (id: string, body: CreateReportSubmissionPayload) =>
    api.put<ReportSubmissionDto>(`/report-submissions/${id}`, body),
  submit: (id: string) =>
    api.post<ReportSubmissionDto>(`/report-submissions/${id}/submit`),
};

export const reportGenerationApi = {
  generate: (body: GenerateReportPayload) =>
    api.post<GenerateReportResult>("/reports/generate", body),

  /** Streams SSE output. Returns an AbortController so callers can cancel. */
  stream: async (
    body: GenerateReportPayload,
    onChunk: (text: string) => void,
    onComplete: (reportId?: string) => void,
    onError: (err: Error) => void,
  ): Promise<AbortController> => {
    const ctrl = new AbortController();
    try {
      const resp = await fetch(`${API_BASE}/reports/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });
      if (!resp.ok || !resp.body) {
        throw new Error(`Streaming failed: ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let reportId: string | undefined;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx: number;
        while ((idx = buffer.indexOf("\n\n")) >= 0) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          for (const line of frame.split("\n")) {
            if (line.startsWith("data: ")) {
              const payload = line.slice(6).replace(/\\n/g, "\n");
              const match = payload.match(/<<PHX_REPORT_ID:([a-f0-9-]{36})>>/i);
              if (match) {
                reportId = match[1];
                continue;
              }
              onChunk(payload);
            }
          }
        }
      }
      onComplete(reportId);
    } catch (err) {
      if ((err as Error).name !== "AbortError") onError(err as Error);
    }
    return ctrl;
  },
};
